import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { MagnifyingGlass, GraduationCap, Flower, HeartStraight, Plus, Users, FunnelSimple, MapPin, CaretRight, X } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

function ObitCard({ m }) {
  const years = [m.dob, m.dop].filter(Boolean).join(" – ");
  const loc = [m.city, m.district, m.region, m.country].filter(Boolean).slice(0, 2).join(", ");
  const img = m.photo_file_id ? fileUrl(m.photo_file_id) : (m.photo_url && (m.photo_url.startsWith("http") ? m.photo_url : fileUrl(m.photo_url)));
  return (
    <Link to={m.link} data-testid={`obituary-card-${m.id}`}
      className="group flex flex-col items-center rounded-2xl border border-slate-100 bg-white p-6 text-center shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md">
      <div className="mb-4 h-28 w-28 overflow-hidden rounded-full ring-4 ring-green-100">
        {img
          ? <img src={img} alt={m.name} className="h-full w-full object-cover" />
          : <span className="grid h-full w-full place-items-center bg-gradient-to-br from-green-50 to-orange-50 text-2xl font-heading text-green-700">{(m.name || "?")[0]}</span>}
      </div>
      <h3 className="font-heading text-lg font-medium">{m.name}</h3>
      {years && <p className="mt-0.5 text-sm text-muted-foreground">{years}</p>}
      {m.institution && <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground"><GraduationCap size={13} /> {m.institution}</p>}
      {loc && <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground"><MapPin size={12} /> {loc}</p>}
      {m.source === "person" && <span className="mt-2 rounded-full bg-secondary/10 px-2 py-0.5 text-[10px] text-secondary">Faculty / Staff</span>}
      {m.epitaph && <p className="mt-3 text-sm italic text-foreground/70">“{m.epitaph}”</p>}
    </Link>
  );
}

const DECADES = ["1950", "1960", "1970", "1980", "1990", "2000", "2010", "2020"];
const LEVEL_LABEL = { country: "Country", region: "State / Province", district: "District / County", city: "City / Town / Village" };

export default function Memorials() {
  useSeo("Obituaries & Memorials", "Remember the people who shaped our journey — classmates, teachers, alumni and friends, browsable by place.");
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [decade, setDecade] = useState("");
  const [geo, setGeo] = useState({ country: "", region: "", district: "", city: "" });
  const [facets, setFacets] = useState({ level: "country", options: [] });

  const geoParams = () => {
    const p = new URLSearchParams();
    Object.entries(geo).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p;
  };

  const loadItems = () => {
    const p = geoParams();
    if (q) p.set("q", q);
    if (decade) p.set("decade", decade);
    api.get(`/public/obituaries?${p.toString()}`).then((r) => setRows(r.data.items || [])).catch(() => setRows([]));
  };
  const loadFacets = () => {
    const p = new URLSearchParams();
    if (geo.country) p.set("country", geo.country);
    if (geo.region) p.set("region", geo.region);
    if (geo.district) p.set("district", geo.district);
    if (q) p.set("q", q);
    if (decade) p.set("decade", decade);
    api.get(`/public/obituaries/geo?${p.toString()}`).then((r) => setFacets(r.data)).catch(() => setFacets({ level: "country", options: [] }));
  };

  useEffect(() => { loadFacets(); /* eslint-disable-next-line */ }, [geo.country, geo.region, geo.district, q, decade]);
  useEffect(() => {
    const t = setTimeout(loadItems, rows === null ? 0 : 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, decade, geo.country, geo.region, geo.district, geo.city]);

  const pick = (level, name) => {
    if (level === "country") setGeo({ country: name, region: "", district: "", city: "" });
    else if (level === "region") setGeo((g) => ({ ...g, region: name, district: "", city: "" }));
    else if (level === "district") setGeo((g) => ({ ...g, district: name, city: "" }));
    else setGeo((g) => ({ ...g, city: name }));
  };
  const resetTo = (level) => {
    if (level === "all") setGeo({ country: "", region: "", district: "", city: "" });
    else if (level === "country") setGeo((g) => ({ ...g, region: "", district: "", city: "" }));
    else if (level === "region") setGeo((g) => ({ ...g, district: "", city: "" }));
    else if (level === "district") setGeo((g) => ({ ...g, city: "" }));
  };

  const crumbs = [
    { label: "Everywhere", key: "all", value: "" },
    { label: geo.country, key: "country", value: geo.country },
    { label: geo.region, key: "region", value: geo.region },
    { label: geo.district, key: "district", value: geo.district },
    { label: geo.city, key: "city", value: geo.city },
  ].filter((c, i) => i === 0 || c.value);

  return (
    <div data-testid="memorials-page">
      <section className="mx-auto max-w-5xl px-5 pt-12 text-center">
        <div className="mx-auto mb-6 overflow-hidden rounded-[2rem] border border-border/60 shadow-sm">
          <img src="/article-covers/memorial-hero.png" alt="Forever in our hearts" className="w-full object-cover" />
        </div>
        <p className="overline text-secondary mb-2">Every institution. Every generation. Every place.</p>
        <h1 className="font-heading text-4xl font-light tracking-tight sm:text-5xl">Obituaries &amp; Memorials</h1>
        <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
          Remember the people who shaped our journey. Browse obituaries and memorials of classmates, teachers, alumni and staff — by country, state, district and city.
        </p>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link to="/memorials/create"><Button className="rounded-full gap-2" data-testid="create-memorial-btn"><Plus size={16} /> Create a Memorial</Button></Link>
          <Link to="/memorials/suggest"><Button variant="outline" className="rounded-full gap-2" data-testid="suggest-memorial-btn"><Users size={16} /> Suggest a Memorial</Button></Link>
        </div>

        <div className="relative mx-auto mt-8 max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a memorial — name or institution…" className="rounded-full pl-10" data-testid="memorial-search" />
        </div>
      </section>

      {/* Geo drill-down feed */}
      <section className="mx-auto max-w-6xl px-5 pt-8">
        <div className="rounded-2xl border border-border/60 bg-muted/30 p-4" data-testid="geo-feed">
          <div className="flex flex-wrap items-center gap-1.5 text-sm" data-testid="geo-breadcrumb">
            <FunnelSimple size={14} className="text-secondary" />
            {crumbs.map((c, i) => (
              <span key={c.key} className="flex items-center gap-1.5">
                {i > 0 && <CaretRight size={12} className="text-muted-foreground" />}
                <button onClick={() => resetTo(c.key)} className={`rounded-full px-2 py-0.5 ${i === crumbs.length - 1 ? "bg-secondary text-secondary-foreground" : "text-primary hover:underline"}`} data-testid={`crumb-${c.key}`}>
                  {i === 0 ? "Everywhere" : c.label}
                </button>
              </span>
            ))}
            {(geo.country || geo.region || geo.district || geo.city) && (
              <button onClick={() => resetTo("all")} className="ml-1 flex items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground" data-testid="geo-clear"><X size={12} /> clear</button>
            )}
          </div>
          {facets.options.length > 0 && (
            <div className="mt-3">
              <p className="mb-1.5 text-xs text-muted-foreground">Browse by {LEVEL_LABEL[facets.level]}</p>
              <div className="flex flex-wrap gap-2">
                {facets.options.map((o) => (
                  <button key={o.name} onClick={() => pick(facets.level, o.name)} data-testid={`geo-opt-${o.name}`}
                    className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs transition-colors hover:border-green-400">
                    <MapPin size={11} className="text-secondary" /> {o.name} <span className="text-muted-foreground">{o.count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="decade-filters">
            <span className="flex items-center gap-1 text-xs text-muted-foreground">Decade</span>
            {DECADES.map((d) => (
              <button key={d} onClick={() => setDecade(decade === d ? "" : d)} data-testid={`decade-${d}`}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${decade === d ? "border-green-600 bg-green-600 text-white" : "border-slate-200 text-muted-foreground hover:border-green-400"}`}>{d}s</button>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-10">
        {rows === null ? <p className="text-center text-muted-foreground">Loading…</p>
          : rows.length === 0 ? (
            <div className="mx-auto max-w-xl rounded-2xl border border-dashed border-border/60 p-12 text-center text-muted-foreground" data-testid="memorials-empty">
              <Flower size={40} weight="duotone" className="mx-auto mb-3 text-secondary" />
              No memorials found here. Be the first to <Link to="/memorials/create" className="text-secondary hover:underline">create a memorial</Link> or <Link to="/memorials/suggest" className="text-secondary hover:underline">suggest one</Link>.
            </div>
          ) : (
            <>
              <p className="mb-4 text-sm text-muted-foreground" data-testid="obituary-count">{rows.length} {rows.length === 1 ? "person" : "people"} remembered</p>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {rows.map((m) => <ObitCard key={`${m.source}-${m.id}`} m={m} />)}
              </div>
            </>
          )}
      </section>

      <section className="mx-auto max-w-3xl px-5 pb-16 text-center">
        <div className="rounded-2xl border border-secondary/20 bg-secondary/5 p-8">
          <HeartStraight size={28} weight="fill" className="mx-auto mb-3 text-secondary" />
          <p className="font-heading text-lg font-light italic text-foreground/80">Remember. Honor. Preserve.</p>
          <p className="mt-2 text-sm text-muted-foreground">Read our <Link to="/memorial-policy" className="text-secondary hover:underline">Memorial &amp; Obituary Policy</Link> to learn how we care for these records.</p>
        </div>
      </section>
    </div>
  );
}
